// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  increment,
  doc,
  setDoc,
  getDoc,
  addDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { useSingleDeviceSession } from "../hooks/useSingleDeviceSession";

export const db = getFirestore();

// ------------------ CONTEXTOS ------------------
export const authContext = createContext();

export const useAuth = () => {
  const context = useContext(authContext);
  if (!context) throw new Error("Añade el AuthProvider en App.js");
  return context;
};

// ------------------ PROVIDER ------------------
export function AuthProvider({ children }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔒 Hook de sesión única
  const { error, showModal, setShowModal } = useSingleDeviceSession();

  // ------------------ AUTENTICACIÓN ------------------
  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const loginWithGoogle = () => {
    const googleProvider = new GoogleAuthProvider();
    return signInWithPopup(auth, googleProvider);
  };

  const resetPassword = (email, actionCodeSettings) =>
    sendPasswordResetEmail(auth, email, actionCodeSettings);

  // ------------------ GUARDAR USUARIO ------------------
  const guardarEnFirebase = async ({ uid, nombre, email, tokens, deviceId }) => {
    try {
      // ⚡ Aseguramos un deviceId válido
      let finalDeviceId = deviceId || localStorage.getItem("deviceId");

      if (!finalDeviceId) {
        console.warn("⚠️ guardarEnFirebase → deviceId no encontrado en props ni en localStorage");
      } else {
        console.log("📌 guardarEnFirebase → usando deviceId:", finalDeviceId);
      }

      const claseId = JSON.parse(localStorage.getItem("claseId")) || "";
      const nombreGym = JSON.parse(localStorage.getItem("gimnasio")) || "";

      const fecha = new Date();
      const nombreMes = new Intl.DateTimeFormat("es-ES", { month: "long" }).format(fecha);
      const mesHoy = nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1);

      const nuevaInformacion = {
        nombre: nombre && nombre.trim() !== "" ? nombre : "Usuario",
        email: email || "",
        activo: false,  // 👈 siempre a false al crear
        mensajes: "",
        uidd: uid,
        puntosTotales: 0,
        puntosSesion: 0,
        tokens,
        ranking: 0,
        nombreGym,
        claseId,
        creadoEn: new Date(),
        mesReseteado: mesHoy,
        deviceId: finalDeviceId || null, // 👈 ahora debería llegar siempre
      };

      console.log("💾 guardarEnFirebase → creando usuario con:", nuevaInformacion);

      const userDocRef = doc(db, "usuarios", uid);
      await setDoc(userDocRef, nuevaInformacion);

      setUserData({ uid, ...nuevaInformacion });
      console.log("✅ Usuario guardado correctamente en Firestore:", { uid, finalDeviceId });
    } catch (error) {
      console.error("❌ Error al guardar en Firebase:", error);
      throw error;
    }
  };


  // ------------------ REGISTRAR DISPOSITIVO ------------------
  const generarDeviceId = () => {
    const fingerprint =
      navigator.userAgent +
      navigator.language +
      window.screen.width +
      window.screen.height;
    return btoa(fingerprint);
  };

  // ------------------ REGISTRAR DISPOSITIVO ------------------
  const registrarDispositivo = async (uid) => {
    try {
      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId = generarDeviceId();
        localStorage.setItem("deviceId", deviceId);
      }

      const deviceRef = doc(db, "deviceRegistry", deviceId);
      const deviceSnap = await getDoc(deviceRef);

      if (deviceSnap.exists()) {
        const data = deviceSnap.data();
        console.log("📱 registrarDispositivo → EXISTE:", { deviceId, tokens: data.tokens });
        await updateDoc(deviceRef, { lastUser: uid });
        return { existe: true, tokens: data.tokens || 0, ref: deviceRef };
      } else {
        console.log("📱 registrarDispositivo → NO EXISTE (nuevo, sin setDoc aún)", { deviceId });
        // 🔑 devolvemos ref pero sin crear doc todavía → se creará en CASO 1
        return { existe: false, tokens: 0, ref: deviceRef };
      }
    } catch (error) {
      console.error("❌ Error al registrar dispositivo:", error);
      return { existe: false, tokens: 0, ref: null };
    }
  };


  // ------------------ VERIFICAR USUARIO ------------------
  const verificarUsuarioEnFirebase = async (uid) => {
    try {
      const userRef = doc(db, "usuarios", uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        return { existe: true, data: snap.data(), ref: userRef };
      }
      return { existe: false, data: null, ref: userRef };
    } catch (error) {
      console.error("❌ Error al verificar usuario en Firebase", error);
      return { existe: false, data: null, ref: null };
    }
  };

  // ------------------ SINCRONIZACIÓN ------------------

  const sincronizarUsuarioYDispositivo = async (uid, nombre, email) => {
    try {
      // 👇 usamos siempre el nombre más actualizado de Firebase Auth
      const displayNameAuth = auth.currentUser?.displayName || nombre || "Usuario";

      // 👇 registrarDispositivo asegura que localStorage tiene el deviceId
      const device = await registrarDispositivo(uid);
      const deviceId = localStorage.getItem("deviceId");
      const usuario = await verificarUsuarioEnFirebase(uid);

      console.log("🔑 sincronizarUsuarioYDispositivo → deviceId obtenido:", deviceId);
      console.log("👤 Nombre recibido:", { displayNameAuth, nombre, email });

      // ==========================
      // CASO 3: Usuario existe + Device nuevo
      // ==========================
      if (usuario.existe && !device.existe) {
        console.log("🟢 CASO 3: Usuario existe + Device nuevo", {
          uid,
          tokensUsuario: usuario.data.tokens,
        });

        if (device.ref) {
          await setDoc(device.ref, {
            deviceId,
            lastUser: uid,
            tokens: usuario.data.tokens || 0,
            registradoEn: new Date(),
          });
          console.log(
            "✅ Device creado con tokens de usuario:",
            usuario.data.tokens || 0
          );
        }

        console.log(
          "📤 Actualizando usuario con deviceId y nombre:",
          displayNameAuth
        );

        await updateDoc(usuario.ref, {
          //activo: false,
          deviceId,
          nombre: displayNameAuth, // 👈 forzamos el nombre correcto
        });

        console.log("🔥 Usuario actualizado tras login:", {
          uid,
          deviceId,
          //activo: false,
          nombre: displayNameAuth,
        });

        localStorage.setItem("Tokens", usuario.data.tokens || 0);
      }

      // ==========================
      // CASO 1: Usuario nuevo + Device nuevo
      // ==========================
      else if (!usuario.existe && !device.existe) {
        // 🚨 Validación defensiva → no guardamos hasta tener nombre real
        if (!displayNameAuth || displayNameAuth === "Usuario") {
          console.warn("⏸ Cancelando CASO 1 → displayName aún no está listo");
          return false;
        }

        console.log("🟢 CASO 1: Usuario nuevo + Device nuevo", {
          uid,
          displayNameAuth,
          email,
        });

        await guardarEnFirebase({
          uid,
          nombre: displayNameAuth,
          email: email || "",
          tokens: 3,
          deviceId,
        });
        console.log(
          "✅ Usuario guardado con tokens: 3 y nombre:",
          displayNameAuth
        );

        if (device.ref) {
          await setDoc(device.ref, {
            deviceId,
            lastUser: uid,
            tokens: 3,
            registradoEn: new Date(),
          });
          console.log("✅ Device creado con tokens: 3");
        }

        localStorage.setItem("Tokens", 3);
      }

      // ==========================
      // CASO 2: Usuario nuevo + Device existente
      // ==========================
      else if (!usuario.existe && device.existe) {
        // 🚨 Validación defensiva también aquí
        if (!displayNameAuth || displayNameAuth === "Usuario") {
          console.warn("⏸ Cancelando CASO 2 → displayName aún no está listo");
          return false;
        }

        console.log("🟢 CASO 2: Usuario nuevo + Device existente", {
          uid,
          displayNameAuth,
          email,
        });

        await guardarEnFirebase({
          uid,
          nombre: displayNameAuth,
          email: email || "",
          tokens: 0,
          deviceId,
        });
        console.log(
          "✅ Usuario guardado con tokens: 0 y nombre:",
          displayNameAuth
        );

        if (device.ref) {
          await updateDoc(device.ref, { tokens: 0 });
          console.log("✅ Device actualizado a tokens: 0");
        }

        localStorage.setItem("Tokens", 0);
      }

      // ==========================
      // CASO 4: Usuario existe + Device existente
      // ==========================
      else if (usuario.existe && device.existe) {
        console.log("🟢 CASO 4: Usuario existe + Device existente", { uid, deviceId });

        // 🔹 Sincronizar device tokens con usuario
        if (device.ref) {
          await updateDoc(device.ref, {
            tokens: usuario.data.tokens || 0,
            lastUser: uid,
          });
          console.log("🔄 Device tokens sincronizados con usuario:", usuario.data.tokens || 0);
        }

        await updateDoc(usuario.ref, {
          deviceId,                // 👈 aseguramos que queda actualizado
          nombre: displayNameAuth,
        });

        console.log("🔥 Usuario actualizado (caso 4):", {
          uid,
          deviceId,
          activo: false,
          tokens: usuario.data.tokens || 0,
        });

        localStorage.setItem("Tokens", usuario.data.tokens || 0);
      }

      return true;
    } catch (error) {
      console.error("❌ Error al sincronizar usuario y dispositivo:", error);
      return false;
    }
  };


  // ------------------ GASTAR TOKEN EN CLASE ------------------
  const gastarTokenEnClase = async (uid) => {
    try {
      const userRef = doc(db, "usuarios", uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return false;

      const dataUser = userSnap.data();
      const tokensUsuario = dataUser.tokens || 0;
      const nombreGym = dataUser.nombreGym || null;

      if (tokensUsuario <= 0) return false;
      if (!nombreGym || typeof nombreGym !== "string") return false;

      const deviceId = localStorage.getItem("deviceId");
      if (!deviceId) return false;
      const dispositivoRef = doc(db, "deviceRegistry", deviceId);

      await Promise.all([
        updateDoc(userRef, { tokens: increment(-1) }),
        updateDoc(dispositivoRef, {
          tokens: increment(-1),
          lastUser: uid   // 👈 añadimos esto
        }),
      ]);

      console.log("✅ Token restado al usuario y device");

      const claseRef = doc(db, "claves", nombreGym);
      const claseSnap = await getDoc(claseRef);

      if (claseSnap.exists() && claseSnap.data().isGym === false) {
        await updateDoc(claseRef, { tokensEntradas: increment(1) });
        console.log(`✅ tokensEntradas incrementado en ${nombreGym}`);
      }

      return true;
    } catch (error) {
      console.error("❌ Error al gastar token en clase:", error);
      return false;
    }
  };

  // ------------------ DAR PROPINA ------------------
  const darPropina = async (uid, gymId, cantidad, nombreUsuario) => {
    try {
      if (!cantidad || cantidad <= 0) return false;

      // 1. Verificar tokens del usuario
      const userRef = doc(db, "usuarios", uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return false;

      const tokensUsuario = userSnap.data().tokens || 0;
      if (tokensUsuario < cantidad) return false;

      // 2. Verificar dispositivo
      const deviceId = localStorage.getItem("deviceId");
      if (!deviceId) return false;
      const dispositivoRef = doc(db, "deviceRegistry", deviceId);

      // 3. Referencia al doc del gym en claves
      const canalRef = doc(db, "claves", gymId);
      const canalSnap = await getDoc(canalRef);
      if (!canalSnap.exists()) return false;

      const data = canalSnap.data();
      const claseId = data?.claseId || "SinClase";
      const nombreGym = data?.nombre || "Anon";

      // 4. Actualizar tokens
      await Promise.all([
        updateDoc(userRef, { tokens: increment(-cantidad) }),
        updateDoc(dispositivoRef, { tokens: increment(-cantidad) }),
        updateDoc(canalRef, { tokensPropinas: increment(cantidad) }),
      ]);

      // 5. Guardar propina
      await addDoc(collection(db, "propinas"), {
        uid,               // usuario que da
        nombreUsuario,     // nombre del usuario que da
        claseId,           // lo leo desde "claves"
        nombreGym,         // lo leo desde "claves"
        cantidad,
        createdAt: serverTimestamp(),
      });

      console.log(`✅ Propina de ${cantidad} tokens enviada a ${nombreGym} (clase: ${claseId})`);
      return true;
    } catch (error) {
      console.error("❌ Error al dar propina:", error);
      return false;
    }
  };

  // ------------------ UTILIDADES ------------------
  const GuardarEnLocal = async (uid) => {
    localStorage.setItem("usuario", JSON.stringify([uid]));
  };

  const Estado = async (uid) => {
    try {
      const userRef = doc(db, "usuarios", uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        const hoy = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

        await updateDoc(userRef, {
          activo: true,
          ultimoAcceso: hoy
        });

        localStorage.setItem("nombre", JSON.stringify(data.nombre));

        setUserData((prev) => ({ ...prev, ...data, activo: true, ultimoAcceso: hoy }));
        return { ...data, activo: true, ultimoAcceso: hoy };
      }
    } catch (error) {
      console.error("❌ Error al actualizar estado en Firebase:", error);
    }
  };



  const EstadoLogout = async () => {
    try {
      const usuarioActualString = localStorage.getItem("usuario");
      if (!usuarioActualString) return;

      const [uid] = JSON.parse(usuarioActualString);
      const userRef = doc(db, "usuarios", uid);
      const puntosGuardados = parseInt(localStorage.getItem("Puntos") || "0", 10);

      // 🔹 Guardamos la última ruta actual en Firestore
      const currentPath = window.location.pathname.toLowerCase();

      await updateDoc(userRef, {
        activo: false,
        nombreGym: "",
        claseId: "",
        puntosTotales: increment(puntosGuardados),
        ultimoAcceso: new Date().toISOString().split("T")[0],
        ultimoContexto: currentPath || "/comprobacion", // 👈 nuevo
      });

      // Limpiar localStorage (lo seguimos limpiando para evitar inconsistencias)
      localStorage.removeItem("gimnasio");
      localStorage.removeItem("claseId");
      localStorage.removeItem("Puntos");

      console.log("🚪 [EstadoLogout] Guardado en Firestore con ultimoContexto:", currentPath);
    } catch (error) {
      console.error("❌ Error en EstadoLogout:", error);
    }
  };



  const listaClaves = (callback) => {
    const ref = collection(db, "claves");
    return onSnapshot(ref, (snapshot) => {
      const gyms = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(gyms);
    });
  };


  const addInfoGym = async (nombreGym = "", claseId = "") => {
    try {
      const usuarioActualString = localStorage.getItem("usuario");
      if (!usuarioActualString) return;

      const [uid] = JSON.parse(usuarioActualString); // uid guardado en localStorage
      const userRef = doc(db, "usuarios", uid); // acceso directo

      // 👇 Forzar "free" si es modo libre (evitar "")
      const nombreGymFinal = nombreGym && nombreGym.trim() !== "" ? nombreGym : "free";
      const claseIdFinal = claseId && claseId.trim() !== "" ? claseId : "free";

      await updateDoc(userRef, {
        nombreGym: nombreGymFinal,
        claseId: claseIdFinal,
      });

      // Actualizar estado local del contexto
      setUserData((prev) => ({
        ...prev,
        nombreGym: nombreGymFinal,
        claseId: claseIdFinal,
      }));
    } catch (error) {
      console.error("❌ Error en addInfoGym:", error);
    }
  };


  const addRanking = async () => {
    try {
      const puntosGuardados = parseInt(localStorage.getItem("Puntos") || "0", 10);
      const usuarioActualString = localStorage.getItem("usuario");
      if (!usuarioActualString) return;

      const usuarioActual = JSON.parse(usuarioActualString);
      const usuariosRef = collection(db, "usuarios");
      const usuariosSnapshot = await getDocs(usuariosRef);

      const usuarioActualDoc = usuariosSnapshot.docs.find(
        (doc) => doc.data().uidd === usuarioActual[0]
      );

      if (usuarioActualDoc && puntosGuardados > 0) {
        await updateDoc(usuarioActualDoc.ref, {
          puntosTotales: increment(puntosGuardados),
        });
        localStorage.setItem("Puntos", "0");
      }
    } catch (error) {
      console.error("❌ Error en addRanking:", error);
    }
  };

  const resetRanking = async () => {
    try {
      const usuariosRef = collection(db, "usuarios");
      const usuariosSnapshot = await getDocs(usuariosRef);

      usuariosSnapshot.forEach(async (docSnap) => {
        try {
          await updateDoc(docSnap.ref, { puntosSesion: 0 });
        } catch (updateError) {
          console.error("❌ Error al resetear puntosSesion:", updateError);
        }
      });

      console.log("✅ Todos los puntosSesion han sido reseteados.");
    } catch (error) {
      console.error("❌ Error en resetRanking:", error);
    }
  };

  const listaUsuarios = async () => {
    const usuariosRef = collection(db, "usuarios");
    const usuariosSnapshot = await getDocs(usuariosRef);
    return usuariosSnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  };

  const enviarEmoji = async (emoji) => {
    try {
      const usuarioActualString = localStorage.getItem("usuario");
      if (!usuarioActualString) return;

      const usuarioActual = JSON.parse(usuarioActualString);
      const usuariosRef = collection(db, "usuarios");
      const usuariosSnapshot = await getDocs(usuariosRef);

      const usuarioActualDoc = usuariosSnapshot.docs.find(
        (doc) => doc.data().uidd === usuarioActual[0]
      );

      if (usuarioActualDoc) {
        await updateDoc(usuarioActualDoc.ref, { mensajes: emoji });
        setTimeout(async () => {
          try {
            await updateDoc(usuarioActualDoc.ref, { mensajes: "" });
          } catch (err) {
            console.error("❌ Error al borrar emoji:", err);
          }
        }, 5000);
      }
    } catch (error) {
      console.error("❌ Error al enviar emoji:", error);
    }
  };

  // ------------------ COMPRAR TOKENS ------------------
  const comprarTokens = async (uid, cantidad, compraData = {}) => {
    try {
      const userRef = doc(db, "usuarios", uid);

      await updateDoc(userRef, { tokens: increment(cantidad) });

      const deviceId = localStorage.getItem("deviceId");
      if (deviceId) {
        const dispositivoRef = doc(db, "deviceRegistry", deviceId);
        const dispositivoSnap = await getDoc(dispositivoRef);
        if (dispositivoSnap.exists()) {
          await updateDoc(dispositivoRef, { tokens: increment(cantidad) });
        }
      }

      const comprasRef = collection(db, "usuarios", uid, "compras");
      await addDoc(comprasRef, {
        cantidad,
        precio: compraData.precio || null,
        transaccionId: compraData.transaccionId || null,
        metodo: compraData.metodo || "PayPal",
        fecha: serverTimestamp(),
      });

      const tokensActuales = parseInt(localStorage.getItem("Tokens") || "0", 10);
      localStorage.setItem("Tokens", tokensActuales + cantidad);

      console.log(`✅ Se añadieron ${cantidad} tokens y se registró la compra`);
      return true;
    } catch (error) {
      console.error("❌ Error al comprar tokens:", error);
      return false;
    }
  };


  // ------------------ ON AUTH STATE CHANGE ------------------

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log("✅ Sesión persistente activada en localStorage");
      })
      .catch((error) => {
        console.error("❌ Error configurando persistencia:", error);
      });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("🔔 [onAuthStateChanged] Disparado");

      if (currentUser) {
        try {
          console.log("👤 [onAuthStateChanged] currentUser detectado:", {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
          });

          const resultado = await sincronizarUsuarioYDispositivo(
            currentUser.uid,
            currentUser.displayName || "Usuario",
            currentUser.email || ""
          );

          if (resultado) {
            const userRef = doc(db, "usuarios", currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const firestoreData = userSnap.data();
              const lastContext = firestoreData.ultimoContexto || "/comprobacion";

              setUserData({
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName,
                ...firestoreData,
              });

              console.log("📍 [onAuthStateChanged] Ruta actual:", window.location.pathname);
              console.log("📝 [onAuthStateChanged] Último contexto Firestore:", lastContext);

              // 🔹 Activar solo si estaba en home o homeuser Y si la ruta actual coincide
              if (
                (lastContext === "/" || lastContext === "/homeuser") &&
                (window.location.pathname === "/" || window.location.pathname === "/homeuser")
              ) {
                await Estado(currentUser.uid);
                console.log("🟢 [onAuthStateChanged] Usuario activado en", lastContext);
              } else {
                console.log("⏸ [onAuthStateChanged] Usuario no activado (comprobacion o ruta neutra)");
              }


            }
          }
        } catch (error) {
          console.error("❌ Error en onAuthStateChanged:", error);
          await signOut(auth);
          setUserData(null);
        }
      } else {
        setUserData(null); // Usuario no autenticado
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);


  // ------------------ SyncHandler ------------------

  useEffect(() => {
    const syncHandler = (event) => {
      if (event.key === "logout") {
        console.log("🔄 Logout detectado en otra pestaña");
        logout();
      }

      if (event.key === "cerrarClase") {
        console.log("🚪 Cerrar clase detectado en otra pestaña");
        localStorage.removeItem("gimnasio");

        // Solo redirigimos si estás en páginas de entreno
        const currentPath = window.location.pathname.toLowerCase();
        if (["/home", "/homeuser"].includes(currentPath)) {
          window.location.href = "/comprobacion";
        }
      }


    };

    window.addEventListener("storage", syncHandler);
    return () => window.removeEventListener("storage", syncHandler);
  }, []);




  // ------------------ RETURN PROVIDER ------------------
  return (
    <authContext.Provider
      value={{
        signup,
        login,
        user: userData,
        logout,
        loading,
        guardarEnFirebase,
        GuardarEnLocal,
        Estado,
        EstadoLogout,
        verificarUsuarioEnFirebase,
        listaClaves,
        addInfoGym,
        addRanking,
        resetRanking,
        listaUsuarios,
        loginWithGoogle,
        resetPassword,
        enviarEmoji,
        registrarDispositivo,
        sincronizarUsuarioYDispositivo,
        gastarTokenEnClase,
        comprarTokens,
        darPropina,
      }}
    >
      {children}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 text-white p-6 rounded-2xl shadow-xl w-80 text-center">
            <h2 className="text-lg font-bold mb-4 text-red-400">
              {error || "Tu cuenta está activa en otro dispositivo"}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

    </authContext.Provider>
  );
}
